import { useState, useMemo, useEffect, useRef } from "react";
import TutorialSpotlight from '../scenes/room/tutorial/TutorialSpotlight';
import { useInteriorTutorial } from '../scenes/room/tutorial/useInteriorTutorial';
import { INTERIOR_TUTORIAL_STEPS, isItemStep, TUTORIAL_ITEM, TUTORIAL_ITEM_CATEGORY } from '../domain/interiorTutorial';
import { practiceBuy } from '../scenes/room/tutorial/practiceRoom';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type {
  FurnitureCatalog,
} from "../models/apiTypes";
import type { MapObject } from "../models/mapObject";
import { getUserData, type UserData } from "../models/user";
import {
  getFurnitureCatalogFurnitureCatalogGet,
  type FurnitureBuyResponse,
} from "../client";
import { handleApiError, createRetryFunction } from "../utils/net/apiError";
import { FURNITURE_CATEGORY_NAMES, getFurnitureThumbPath, isFurnitureAvailable, isFurnitureSoldOut, isFurnitureCategoryInSeason, compareFurnitureCategories, getFurnitureName } from "../data/furniture";
import { useUserData } from "../hooks/useUserData";
import posthog from "posthog-js";
import { impactOccurred } from "../utils/telegram/hapticFeedback";
import { showAlert } from "../utils/telegram/alert";
import ScreenHeaderWithTabs from "../components/ScreenHeaderWithTabs";
import { useCreateUserAction } from "../hooks/useCreateUserAction";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import useInterfaceStore from "../stores/useInterfaceStore";
import useFurniturePreviewStore from "../stores/useFurniturePreviewStore";
import InsufficientPearlsModal, { InsufficientCurrencyError } from "../ui/modals/InsufficientPearlsModal";
import PearlsBalance from "../components/PearlsBalance";
import { EmptyState, ScreenError, ScreenLoading } from "../ui/primitives/ScreenStatus";
import GameScreenFrame from "../ui/primitives/GameScreenFrame";
import FurnitureShopCategoryPills, {
  type FurnitureShopCategoryType,
} from "./furnitureShop/FurnitureShopCategoryPills";
import FurnitureShopItemCard from "./furnitureShop/FurnitureShopItemCard";
import FurnitureShopMayorBanner from "./furnitureShop/FurnitureShopMayorBanner";
import {
  buyFurnitureViaClient,
  fetchHouseFurniture,
  getCountByCatalog,
  isInvoiceLinkResponse,
  syncPurchasedFurnitureAfterBuy,
} from "./furnitureShop/furnitureShopApi";


type CategoryType = FurnitureShopCategoryType;

interface FurnitureShopScreenProps {
  mapObjectId: number;
  /** Back to the room under it (the shop is a panel over the room, not a route). */
  onClose: () => void;
}

export default function FurnitureShopScreen({ mapObjectId, onClose }: FurnitureShopScreenProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { createUserAction } = useCreateUserAction();
  const setJustPlacedFurnitureCatalogId = useInterfaceStore((s) => s.setJustPlacedFurnitureCatalogId);
  const { setPreviewFurniture } = useFurniturePreviewStore();
  const { data: userData } = useUserData();
  const isAdmin = userData?.is_admin ?? false;
  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryType>("all");
  // Interior tutorial (practice room): on its "buy" phase the shop opens the item's
  // category, puts the item first and points at its Buy; the rest steps back.
  const tutorial = useInteriorTutorial(mapObjectId);
  const tutorialStep = tutorial.step;
  const tutorialItemId = tutorial.phase === 'buy' && isItemStep(tutorialStep) ? TUTORIAL_ITEM[tutorialStep] : null;
  const tutorialCategory = tutorialItemId != null && isItemStep(tutorialStep)
    ? (TUTORIAL_ITEM_CATEGORY[tutorialStep] as CategoryType)
    : undefined;
  const [tutorialCategoryApplied, setTutorialCategoryApplied] = useState<string | null>(null);
  if (tutorialCategory && tutorialCategoryApplied !== tutorialCategory) {
    setTutorialCategoryApplied(tutorialCategory);
    setSelectedCategory(tutorialCategory);
  }
  const [insufficientCurrencyError, setInsufficientCurrencyError] = useState<InsufficientCurrencyError | null>(null);
  const [errorFurnitureItem, setErrorFurnitureItem] = useState<FurnitureCatalog | null>(null);
  const hasCreatedActionRef = useRef<boolean>(false);

  // Кнопка "Назад" (Telegram або fallback)
  const handleBack = onClose;

  useTelegramBackButton(true, handleBack);
  
  // Створюємо екшен для квесту "Visit Shop" при відкритті екрану (фоново, тільки один раз)
  useEffect(() => {
    if (hasCreatedActionRef.current) return;
    hasCreatedActionRef.current = true;
    
    // Reuse existing quest action to avoid backend/type mismatch
    createUserAction('visit_shop').catch((error) => {
      console.error("Failed to create furniture shop action:", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount; createUserAction is new each render
  }, []); // Порожній масив - виконується тільки один раз при монтуванні
  
  // Track furniture category views (fires on mount and on every category change)
  useEffect(() => {
    posthog.capture('furniture_category_selected', { category: selectedCategory });
  }, [selectedCategory]);

  // Determine building type from map objects cache
  const mapObjectType = queryClient.getQueryData<MapObject[]>(['mapObjects'])?.find(o => o.id === mapObjectId)?.type;
  const buildingType = mapObjectType === 'cityhall' ? 'cityhall' : 'house';

  // Завантажуємо дані при відкритті екрану (якщо їх немає в кеші)
  const furnitureCatalogCached = queryClient.getQueryData<FurnitureCatalog[]>(["furnitureCatalog", buildingType]);

  // Завантажуємо дані при відкритті екрану
  const { data, error, isLoading } = useQuery<FurnitureCatalog[]>({
    queryKey: ["furnitureCatalog", buildingType],
    queryFn: async () => {
      const { data } = await getFurnitureCatalogFurnitureCatalogGet({
        query: buildingType ? { building_type: buildingType } : undefined,
        throwOnError: true,
      });
      return data.items;
    },
    initialData: furnitureCatalogCached, // Використовуємо дані з кешу одразу якщо є
    placeholderData: furnitureCatalogCached, // Використовуємо дані з кешу як placeholder
    retry: createRetryFunction(1),
    enabled: true,
  });

  // Використовуємо дані з запиту або з кешу
  const furnitureCatalog = data || furnitureCatalogCached;

  // Фільтрація товарів по категорії та сортування за ціною (від найдешевшого до найдорожчого)
  const filteredItems = useMemo(() => {
    if (!furnitureCatalog) return [];
    
    let items = selectedCategory === "all" 
      ? furnitureCatalog 
      : furnitureCatalog.filter(item => item.category === selectedCategory);
    
    // Доступні товари + розпродані (показуємо з позначкою «Sold out»);
    // сезонні категорії (Halloween) існують лише у свої місяці.
    items = items.filter(item =>
      (isFurnitureAvailable(item) || isFurnitureSoldOut(item)) && isFurnitureCategoryInSeason(item.category));
    
    // Сортуємо за ID від більших до менших (нові меблі зверху), розпродані — в кінці
    items = [...items].sort((a, b) => {
      const soldOutDiff = Number(isFurnitureSoldOut(a)) - Number(isFurnitureSoldOut(b));
      if (soldOutDiff !== 0) return soldOutDiff;
      return (b.id ?? 0) - (a.id ?? 0);
    });
    
    if (tutorialItemId != null) {
      const i = items.findIndex((item) => item.id === tutorialItemId);
      if (i > 0) items = [items[i], ...items.slice(0, i), ...items.slice(i + 1)];
    }
    return items;
  }, [furnitureCatalog, selectedCategory, tutorialItemId]);

  // Get all unique categories from catalog
  const availableCategories = useMemo(() => {
    if (!furnitureCatalog) return [];
    const categories = new Set<string>();
    furnitureCatalog.forEach(item => {
      if (item.category && isFurnitureAvailable(item) && isFurnitureCategoryInSeason(item.category)) {
        categories.add(item.category);
      }
    });
    return Array.from(categories).sort(compareFurnitureCategories);
  }, [furnitureCatalog]);

  // Get translated category names map
  const categoryNamesMap = useMemo(() => FURNITURE_CATEGORY_NAMES(t), [t]);

  // Поступовий рендеринг товарів
  const [visibleItemsCount, setVisibleItemsCount] = useState(5);
  const [prevCategoryForVisible, setPrevCategoryForVisible] = useState(selectedCategory);
  // (b) Reset progressive render count when category changes — during render
  if (selectedCategory !== prevCategoryForVisible) {
    setPrevCategoryForVisible(selectedCategory);
    setVisibleItemsCount(5);
  }

  useEffect(() => {
    if (visibleItemsCount >= filteredItems.length) return;
    
    // Додаємо по 5 елементів кожні 50ms
    const timer = setTimeout(() => {
      setVisibleItemsCount(prev => Math.min(prev + 5, filteredItems.length));
    }, 50);
    
    return () => clearTimeout(timer);
  }, [visibleItemsCount, filteredItems.length]);

  const visibleItems = filteredItems.slice(0, visibleItemsCount);

  // Buying one thing must not throw away what the player is still trying on
  // (previews are kept per house — useFurniturePreviewStore.previewMapObjectId).
  const returnToRoomAfterPurchase = onClose;

  // Practice room: the item appears in the room at once — nothing is charged or sent.
  const buyInPractice = (furniture: FurnitureCatalog) => {
    impactOccurred('medium');
    practiceBuy(queryClient, furniture);
    setJustPlacedFurnitureCatalogId(furniture.id);
    returnToRoomAfterPurchase();
  };

  const handleBuyFree = async (furniture: FurnitureCatalog) => {
    if (tutorial.active) {
      buyInPractice(furniture);
      return;
    }
    setLoadingItemId(furniture.id);
    impactOccurred('light');
    posthog.capture('furniture_buy_attempted', {
      furniture_id: furniture.id,
      furniture_name: getFurnitureName(furniture.id, t),
      furniture_category: furniture.category,
      currency: 'free',
      price: 0,
      map_object_id: mapObjectId,
    });

    try {
      const beforeItems = await fetchHouseFurniture(mapObjectId);
      const previousCount = getCountByCatalog(beforeItems, furniture.id);

      const result = await buyFurnitureViaClient(mapObjectId, furniture.id);
      createUserAction('buy_furniture').catch(() => {});

      posthog.capture('furniture_purchased', {
        furniture_id: furniture.id,
        furniture_name: getFurnitureName(furniture.id, t),
        furniture_category: furniture.category,
        currency: 'free',
        price: 0,
        map_object_id: mapObjectId,
      });

      // Update balance immediately using the value returned by the backend
      if (!isInvoiceLinkResponse(result) && result.remaining_currency !== undefined) {
        queryClient.setQueryData<UserData>(["userData"], (old) =>
          old ? { ...old, currency_balance: result.remaining_currency as number } : old
        );
      } else {
        queryClient.removeQueries({ queryKey: ["userData"] });
        queryClient.prefetchQuery({ queryKey: ["userData"], queryFn: getUserData });
      }

      await syncPurchasedFurnitureAfterBuy(queryClient, mapObjectId, furniture.id, previousCount);
      await queryClient.invalidateQueries({ queryKey: ["houseFurniture", mapObjectId] });
      await queryClient.refetchQueries({ queryKey: ["houseFurniture", mapObjectId] });
      setJustPlacedFurnitureCatalogId(furniture.id);
      returnToRoomAfterPurchase();

    } catch (error: unknown) {
      console.error("Purchase error:", error);
      const errorMessage = error instanceof Error ? error.message : t("Failed to purchase furniture.");
      showAlert(errorMessage);
    } finally {
      setLoadingItemId(null);
    }
  };

  const handleBuy = async (furniture: FurnitureCatalog) => {
    if (tutorial.active) {
      buyInPractice(furniture);
      return;
    }
    setLoadingItemId(furniture.id);
    impactOccurred('light');
    posthog.capture('furniture_buy_attempted', {
      furniture_id: furniture.id,
      furniture_name: getFurnitureName(furniture.id, t),
      furniture_category: furniture.category,
      currency: furniture.currency,
      price: furniture.price,
      map_object_id: mapObjectId,
    });

    try {
      const beforeItems = await fetchHouseFurniture(mapObjectId);
      const previousCount = getCountByCatalog(beforeItems, furniture.id);

      const result = await buyFurnitureViaClient(mapObjectId, furniture.id);
      createUserAction('buy_furniture').catch(() => {});

      // Stars payment — backend returns invoice_link instead of furniture
      if (isInvoiceLinkResponse(result)) {
        const tg = window.Telegram?.WebApp;
        tg?.openInvoice?.(result.invoice_link, async (status: string) => {
          if (status === 'paid') {
            await syncPurchasedFurnitureAfterBuy(queryClient, mapObjectId, furniture.id, previousCount);
            queryClient.invalidateQueries({ queryKey: ["houseFurniture", mapObjectId] });
            setJustPlacedFurnitureCatalogId(furniture.id);
            returnToRoomAfterPurchase();
          }
        });
        setLoadingItemId(null);
        return;
      }

      posthog.capture('furniture_purchased', {
        furniture_id: furniture.id,
        furniture_name: getFurnitureName(furniture.id, t),
        furniture_category: furniture.category,
        currency: furniture.currency,
        price: furniture.price,
        map_object_id: mapObjectId,
      });

      const buyResult = result as FurnitureBuyResponse;
      if (buyResult.remaining_currency !== undefined) {
        queryClient.setQueryData<UserData>(["userData"], (old) =>
          old ? { ...old, currency_balance: buyResult.remaining_currency as number } : old
        );
      } else {
        queryClient.removeQueries({ queryKey: ["userData"] });
        queryClient.prefetchQuery({ queryKey: ["userData"], queryFn: getUserData });
      }

      await syncPurchasedFurnitureAfterBuy(queryClient, mapObjectId, furniture.id, previousCount);
      await queryClient.invalidateQueries({ queryKey: ["houseFurniture", mapObjectId] });
      await queryClient.refetchQueries({ queryKey: ["houseFurniture", mapObjectId] });
      setJustPlacedFurnitureCatalogId(furniture.id);
      returnToRoomAfterPurchase();

    } catch (error: unknown) {
      const err = handleApiError(error, { logLabel: 'furnitureBuy' });
      if (err.errorType === 'insufficient_currency') {
        setInsufficientCurrencyError({
          error_type: 'insufficient_currency',
          required_pearls: err.extra.required_pearls ?? null,
          available_pearls: err.extra.available_pearls ?? null,
          required_stars: err.extra.required_stars ?? null,
          available_stars: err.extra.available_stars ?? null,
        });
        setErrorFurnitureItem(furniture);
        return;
      }
      showAlert(err.detail || t("Failed to purchase furniture. Check your balance."));
    } finally {
      setLoadingItemId(null);
    }
  };

  const handleTryFurniture = (furniture: FurnitureCatalog) => {
    impactOccurred('light');
    setPreviewFurniture(mapObjectId, furniture);
    onClose();
  };

  if (isLoading) {
    return (
      <ScreenLoading label={t("Loading...")} />
    );
  }

  if (error) {
    return (
      <ScreenError
        title={t("Error loading furniture catalog")}
        detail={error instanceof Error ? error.message : String(error)}
      />
    );
  }

  return (
    <>
    {tutorialItemId != null && tutorialStep && (
      <TutorialSpotlight target={`shop-buy-${tutorialItemId}`} gesture="tap" caption={t("Tap Buy. It's practice, nothing is charged")}
        onSkip={tutorial.skip} onSkipStep={tutorial.advance} stepKey={`shop:${tutorialItemId}`}
        progress={{ done: INTERIOR_TUTORIAL_STEPS.indexOf(tutorialStep), total: INTERIOR_TUTORIAL_STEPS.length - 1 }} />
    )}
    <GameScreenFrame
      showNavigation={false}
      afterContent={(
        <InsufficientPearlsModal
          error={insufficientCurrencyError}
          onClose={() => {
            setInsufficientCurrencyError(null);
            setErrorFurnitureItem(null);
          }}
          requiredLabel={t("To purchase this furniture, you need:")}
          onGetPearls={() => {
            setInsufficientCurrencyError(null);
            setErrorFurnitureItem(null);
            navigate('/shop?category=pearls');
          }}
          preview={errorFurnitureItem ? (
            <div className="mb-xl flex justify-center">
              <div className="flex h-[100px] w-[100px] items-center justify-center rounded-lg bg-[image:var(--gradient-item-spotlight)]">
                <img
                  src={getFurnitureThumbPath(errorFurnitureItem)}
                  alt={getFurnitureName(errorFurnitureItem.id, t)}
                  className="h-[80px] w-[80px] object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            </div>
          ) : undefined}
        />
      )}
    >
        <ScreenHeaderWithTabs
          title={t("Shop")}
          tabs={[]}
          selectedTab=""
          onTabChange={() => {}}
          scrollable={false}
        />

        <PearlsBalance variant="dark" padding="8px 20px 0 20px" />

        {/* Кнопка "Назад" (fallback якщо Telegram back button не видима) */}
        {!window.Telegram?.WebApp?.BackButton?.isVisible && (
          <button
            onClick={handleBack}
            className="absolute top-xl left-xl z-[1000] flex cursor-pointer items-center gap-sm rounded-md border border-solid border-white-alpha-30 bg-black-alpha-60 px-xl py-md text-[14px] font-semibold text-white"
          >
            ← {t("Back")}
          </button>
        )}

        {/* City Hall special shop banner */}
        {buildingType === 'cityhall' && (
          <FurnitureShopMayorBanner />
        )}

        {/* Scrollable category pills */}
        <FurnitureShopCategoryPills
          selectedCategory={selectedCategory}
          availableCategories={availableCategories}
          categoryNamesMap={categoryNamesMap}
          onSelectCategory={tutorial.active ? () => {} : setSelectedCategory}
        />

        {/* Контент з товарами */}
        <div className="box-border flex min-h-0 flex-1 flex-col items-center gap-3xl px-2xl pt-3xl pb-[var(--bottom-nav-height)]">
          {!filteredItems || filteredItems.length === 0 ? (
            <EmptyState>
              {t("No furniture available")}
            </EmptyState>
          ) : (
            visibleItems.map((furniture) => (
              <FurnitureShopItemCard
                key={furniture.id}
                furniture={furniture}
                isLoading={loadingItemId === furniture.id}
                isAdmin={isAdmin}
                onTry={handleTryFurniture}
                onBuyFree={handleBuyFree}
                onBuy={handleBuy}
                practice={tutorial.active ? (furniture.id === tutorialItemId ? 'target' : 'dimmed') : undefined}
              />
            ))
          )}
            {/* Фіксований буфер, який не стискається */}
            <div className="h-[var(--bottom-nav-height)] min-h-[var(--bottom-nav-height)] w-full shrink-0" />
        </div>
    </GameScreenFrame>
    </>
  );
}
